import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import {
  Form,
  Input,
  InputNumber,
  Card,
  Typography,
  Space,
  Alert,
  Upload,
  Table,
  Button,
  message,
} from "antd";
import type { UploadProps } from "antd/es/upload/interface";
import { Calculator } from "lucide-react";
import {
  UploadOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import Papa from "papaparse";
import ColumnMappingModal from "../../../components/ColumnMappingModal";
import {
  validateGeoLiftData,
  convertMappingsToBackendFormat,
  type ColumnMapping,
  type ValidationResult,
} from "../../../utils/dataValidation";
import CustomExcelIcon from "../../../assets/icons/CustomExcelIcon";

const { Title, Text } = Typography;

interface Step1Props {
  onValuesChange?: (values: any) => void;
  initialValues?: any;
  setFileUploaded?: (value: boolean) => void;
}

const Step1ConfigureExperiment = forwardRef<any, Step1Props>(
  ({ onValuesChange, initialValues, setFileUploaded }, ref) => {
    const [form] = Form.useForm();
    const [fileList, setFileList] = useState<File[]>([]);
    const [csvData, setCsvData] = useState<any[]>([]);
    const [columns, setColumns] = useState<
      { title: string; dataIndex: string }[]
    >([]);
    const [loading, setLoading] = useState(false);
    const [validationResult, setValidationResult] =
      useState<ValidationResult | null>(null);
    const [showMappingModal, setShowMappingModal] = useState(true);
    const [pendingFile, setPendingFile] = useState<File | null>(null);
    const [columnMappings, setColumnMappings] = useState<{
      [key: string]: string | null;
    }>({});

    useEffect(() => {
      if (initialValues) {
        form.setFieldsValue(initialValues);
        if (initialValues.fileList) {
          setFileList(initialValues.fileList);
        }
      }
    }, [initialValues, form]);

    useImperativeHandle(ref, () => ({
      submitForm: () => {
        form.submit();
      },
      getValues: () => {
        return {
          ...form.getFieldsValue(),
          fileList,
        };
      },
    }));

    const handleValuesChange = (_changedValues: any, allValues: any) => {
      if (onValuesChange) {
        onValuesChange({
          ...allValues,
          fileList,
          columnMappings,
        });
      }
    };

    // Helper function to process file after mappings are confirmed
    const processFile = async (
      file: File,
      mappings: { [key: string]: string | null }
    ) => {
      setLoading(true);
      try {
        await new Promise((resolve, reject) => {
          Papa.parse(file, {
            complete: (result) => {
              if (result.data.length > 0) {
                const headers = result.data[0] as string[];
                const generatedColumns = headers.map((header) => ({
                  title: header,
                  dataIndex: header.toLowerCase().replace(/\s+/g, "_"),
                }));

                const tableData = result.data
                  .slice(1)
                  .map((row: any, index) => ({
                    key: index,
                    ...headers.reduce((acc, header, i) => {
                      acc[header.toLowerCase().replace(/\s+/g, "_")] = row[i];
                      return acc;
                    }, {} as Record<string, any>),
                  }));

                setColumns(generatedColumns);
                setCsvData(tableData);
                setFileList([file]);
                setColumnMappings(mappings);

                if (setFileUploaded) {
                  setFileUploaded(true);
                }

                message.success(
                  `${file.name} uploaded and mapped successfully`
                );

                // Trigger onValuesChange with new file and mappings
                if (onValuesChange) {
                  onValuesChange({
                    ...form.getFieldsValue(),
                    fileList: [file],
                    columnMappings: mappings,
                  });
                }

                resolve(true);
              } else {
                message.error("Empty CSV or invalid format.");
                if (setFileUploaded) {
                  setFileUploaded(false);
                }
                reject(new Error("Empty CSV"));
              }
            },
            error: (error) => {
              console.error("CSV parsing error:", error);
              setCsvData([]);
              if (setFileUploaded) {
                setFileUploaded(false);
              }
              reject(error);
            },
            skipEmptyLines: true,
          });
        });
      } catch (error) {
        console.error("Upload failed:", error);
        message.error("File upload failed");
        if (setFileUploaded) {
          setFileUploaded(false);
        }
        throw error;
      } finally {
        setLoading(false);
      }
    };

    // Handle mapping confirmation from modal
    const handleMappingConfirm = async (mappings: ColumnMapping[]) => {
      if (!pendingFile) {
        message.error("No file selected");
        return;
      }

      const backendMappings = convertMappingsToBackendFormat(mappings);
      setShowMappingModal(false);

      try {
        await processFile(pendingFile, backendMappings);
      } catch (error) {
        console.error("Upload failed after mapping:", error);
      }
    };

    // Handle mapping cancellation
    const handleMappingCancel = () => {
      setShowMappingModal(false);
      setPendingFile(null);
      setValidationResult(null);
      message.info("File upload cancelled");
    };

    const uploadProps: UploadProps = {
      name: "file",
      accept: ".csv",
      beforeUpload: async (file) => {
        setLoading(true);
        try {
          await new Promise((resolve, reject) => {
            Papa.parse(file, {
              preview: 1,
              header: true,
              complete: (result) => {
                if (result.meta.fields && result.meta.fields.length > 0) {
                  const headers = result.meta.fields;
                  const validation = validateGeoLiftData(headers);
                  setValidationResult(validation);
                  setPendingFile(file);
                  if (validation.needsMapping || !validation.isValid) {
                    setShowMappingModal(true);
                    setLoading(false);
                    resolve(true);
                  } else {
                    const mappings = convertMappingsToBackendFormat(
                      validation.mappings
                    );
                    processFile(file, mappings).then(resolve).catch(reject);
                  }
                } else {
                  message.error("Empty CSV or invalid format.");
                  if (setFileUploaded) {
                    setFileUploaded(false);
                  }
                  setLoading(false);
                  reject(new Error("Empty CSV"));
                }
              },
              error: (error) => {
                console.error("CSV parsing error:", error);
                if (setFileUploaded) {
                  setFileUploaded(false);
                }
                setLoading(false);
                reject(error);
              },
              skipEmptyLines: true,
            });
          });
        } catch (error) {
          console.error("Upload failed:", error);
          message.error("File upload failed");
          if (setFileUploaded) {
            setFileUploaded(false);
          }
          setLoading(false);
        }

        return false;
      },
      onRemove: () => {
        setFileList([]);
        setCsvData([]);
        if (setFileUploaded) {
          setFileUploaded(false);
        }
      },
      showUploadList: false,
    };

    return (
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Calculator className="w-8 h-8 text-blue-600" />
            <Title level={2} className="mb-0">
              Configure Experiment Parameters
            </Title>
          </div>
          <Text type="secondary" className="text-base">
            Set up the basic parameters for your power calculation
          </Text>
        </div>

        <Card className="shadow-sm">
          <Form
            form={form}
            layout="vertical"
            onValuesChange={handleValuesChange}
            initialValues={{
              significanceLevel: 0.05,
              statisticalPower: 0.8,
              ...initialValues,
            }}
          >
            <Space direction="vertical" size="large" className="w-full">
              <Form.Item
                label={<span className="font-semibold">Experiment Name</span>}
                name="experimentName"
                rules={[
                  { required: true, message: "Please enter experiment name" },
                ]}
              >
                <Input
                  placeholder="e.g., Q1 Marketing Campaign Test"
                  size="large"
                />
              </Form.Item>

              <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 mb-4">
                <div className="flex items-start">
                  <CustomExcelIcon className="text-blue-500 text-xl mt-1 mr-3" />
                  <div>
                    <Text strong className="text-blue-700 block mb-1">
                      Upload Historical Data
                    </Text>
                    <Text className="text-blue-600 block text-sm">
                      • Upload a CSV file containing historical marketing
                      performance data (date, location, KPI).
                      <br />• Format: <strong>CSV only</strong>, with column
                      headers in the first row.
                      <br />• <strong>Download the sample file below</strong> to
                      ensure your format matches the expected structure.
                    </Text>
                    <Button
                      type="link"
                      className="text-blue-600 p-0 mt-2 flex items-center"
                      href="/sample-geo-lift.csv"
                      download
                    >
                      <CustomExcelIcon className="mr-1" /> Download Sample CSV
                    </Button>
                  </div>
                </div>
              </div>

              <div className="space-y-4 mt-6">
                <label className="block font-medium text-gray-700 mb-2">
                  Upload Data File <span className="text-red-500">*</span>
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 bg-gray-50 hover:bg-gray-100 transition-colors">
                  <Upload
                    {...uploadProps}
                    className="w-full flex justify-center"
                  >
                    <div className="text-center">
                      <UploadOutlined className="text-2xl text-blue-500 mb-2" />
                      <div className="text-gray-700 font-medium mb-1">
                        Click or drag file to this area to upload
                      </div>
                      <p className="text-gray-500 text-sm">
                        Support for a single CSV file only
                      </p>
                    </div>
                  </Upload>
                </div>

                {fileList.length > 0 && (
                  <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <div className="flex items-center">
                      <CustomExcelIcon className="text-blue-500 mr-2" />
                      <span className="text-sm text-gray-700">
                        {fileList[0].name}
                      </span>
                    </div>
                    <Button
                      type="text"
                      danger
                      onClick={() => {
                        setFileList([]);
                        setCsvData([]);
                        if (setFileUploaded) {
                          setFileUploaded(false);
                        }
                      }}
                      icon={<CloseOutlined />}
                    >
                      Remove
                    </Button>
                  </div>
                )}

                {csvData.length > 0 && (
                  <div className="w-full mt-6">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-lg font-medium text-gray-700">
                        Data Preview
                      </h3>
                      <Button
                        type="text"
                        onClick={() => {
                          setCsvData([]);
                        }}
                        icon={<CloseOutlined />}
                      >
                        Hide Preview
                      </Button>
                    </div>
                    <Table
                      columns={columns}
                      loading={loading}
                      dataSource={csvData.slice(0, 5)}
                      pagination={false}
                      scroll={{ x: true }}
                      bordered
                      size="small"
                      className="w-full shadow-sm"
                    />
                  </div>
                )}
              </div>

              <Alert
                message="Budget Information"
                description="Provide your campaign budget and current marketing spend to calculate optimal experiment design."
                type="info"
                showIcon
                className="mb-4"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Form.Item
                  label={
                    <span className="font-semibold">Campaign Budget ($)</span>
                  }
                  name="campaignBudget"
                  rules={[
                    { required: true, message: "Please enter campaign budget" },
                  ]}
                  tooltip="The total budget allocated for the experimental campaign"
                >
                  <InputNumber
                    min={0}
                    step={1000}
                    size="large"
                    className="w-full"
                    placeholder="e.g., 100000"
                    formatter={(value) =>
                      `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    }
                    parser={(value) => value!.replace(/\$\s?|(,*)/g, "") as any}
                  />
                </Form.Item>

                <Form.Item
                  label={
                    <span className="font-semibold">
                      BAU - Business As Usual Budget ($)
                    </span>
                  }
                  name="bauBudget"
                  rules={[
                    { required: true, message: "Please enter BAU budget" },
                  ]}
                  tooltip="Your existing marketing budget being spent (baseline spending)"
                >
                  <InputNumber
                    min={0}
                    step={1000}
                    size="large"
                    className="w-full"
                    placeholder="e.g., 50000"
                    formatter={(value) =>
                      `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    }
                    parser={(value) => value!.replace(/\$\s?|(,*)/g, "") as any}
                  />
                </Form.Item>
              </div>

              <Form.Item
                label={
                  <span className="font-semibold">
                    AOV - Average Order Value ($)
                  </span>
                }
                name="averageOrderValue"
                rules={[
                  { required: true, message: "Please enter average order value" },
                ]}
                tooltip="The average dollar amount per customer transaction or order"
              >
                <InputNumber
                  min={0}
                  step={1}
                  precision={2}
                  size="large"
                  className="w-full"
                  placeholder="e.g., 75.50"
                  formatter={(value) =>
                    `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value!.replace(/\$\s?|(,*)/g, "") as any}
                />
              </Form.Item>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Form.Item
                  label={
                    <span className="font-semibold">
                      Target CPIC - Cost Per Incremental $ ($)
                    </span>
                  }
                  name="targetCPIC"
                  rules={[
                    { required: true, message: "Please enter target CPIC" },
                  ]}
                  tooltip="Cost to generate $1 of incremental sales (typically 0.01 to 1.0). Example: 0.07 means $0.07 investment generates $1 sales"
                >
                  <InputNumber
                    min={0}
                    max={10}
                    step={0.01}
                    precision={2}
                    size="large"
                    className="w-full"
                    placeholder="e.g., 0.07"
                    formatter={(value) => `$ ${value}`}
                    parser={(value) => value!.replace(/\$\s?/g, "") as any}
                  />
                </Form.Item>

                <Form.Item
                  label={
                    <span className="font-semibold">
                      Statistical Power (1 - β)
                    </span>
                  }
                  name="statisticalPower"
                  rules={[
                    {
                      required: true,
                      message: "Please enter statistical power",
                    },
                  ]}
                  tooltip="The probability of correctly detecting an effect when it exists. Standard: 0.8 (80%)"
                >
                  <InputNumber
                    min={0.5}
                    max={0.99}
                    step={0.05}
                    size="large"
                    className="w-full"
                    placeholder="e.g., 0.8"
                  />
                </Form.Item>
              </div>

              <Alert
                message="GeoLift Test Configuration"
                description="Configure which market to test and the duration of your experiment."
                type="info"
                showIcon
                className="mb-4"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Form.Item
                  label={
                    <span className="font-semibold">
                      Test Market (Include Market)
                    </span>
                  }
                  name="includeMarket"
                  rules={[
                    { required: true, message: "Please enter test market" },
                  ]}
                  tooltip="Enter the market you want to test in your GeoLift experiment (e.g., OK, NC, TX)"
                >
                  <Input
                    size="large"
                    placeholder="e.g., OK"
                    className="w-full"
                  />
                </Form.Item>

                <Form.Item
                  label={
                    <span className="font-semibold">
                      Treatment Duration (Weeks)
                    </span>
                  }
                  name="treatmentDuration"
                  rules={[
                    {
                      required: true,
                      message: "Please enter treatment duration",
                    },
                  ]}
                  tooltip="Number of weeks the test will run"
                  initialValue={9}
                >
                  <InputNumber
                    min={1}
                    max={52}
                    size="large"
                    className="w-full"
                    placeholder="e.g., 9"
                  />
                </Form.Item>
              </div>

              <Form.Item
                label={
                  <span className="font-semibold">
                    Effect Sizes to Test (%)
                  </span>
                }
                name="effectSizes"
                tooltip="Multiple effect sizes to test (e.g., 10%, 25%, 50%). Enter as comma-separated values."
                initialValue="10,25,50"
              >
                <Input
                  size="large"
                  placeholder="e.g., 10,25,50"
                  className="w-full"
                />
              </Form.Item>
            </Space>
          </Form>
        </Card>

        {/* Column Mapping Modal */}
        {validationResult && (
          <ColumnMappingModal
            visible={showMappingModal}
            validationResult={validationResult}
            title="Map Your Power Calculator Data Columns"
            onConfirm={handleMappingConfirm}
            onCancel={handleMappingCancel}
            confirmText="Confirm & Continue"
          />
        )}
      </div>
    );
  }
);

Step1ConfigureExperiment.displayName = "Step1ConfigureExperiment";

export default Step1ConfigureExperiment;
